import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiCheck, FiHeart, FiMinus, FiPlus } from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, isWishlisted } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setSelectedSize(product?.sizes?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setQuantity(1);
    setAdded(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return productData
      .filter(
        (item) => item.id !== product.id && item.category === product.category
      )
      .slice(0, 3);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail-not-found">
        <p className="eyebrow">Collection</p>
        <h1>Product not found</h1>
        <p>The piece may have moved or is no longer part of this edit.</p>
        <Link className="solid-button" to="/shop">
          Back to shop
        </Link>
      </main>
    );
  }

  const handleAddToCart = () => {
    if (!product.inStock) return;
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
  };

  return (
    <main className="product-detail">
      <div className="product-detail-layout">
        <section className="product-gallery" aria-label={`${product.name} gallery`}>
          {product.images.map((image, index) => (
            <figure key={image} className={index === 0 ? "product-gallery-primary" : ""}>
              <img
                src={image}
                alt={`${product.name}${index ? `, view ${index + 1}` : ""}`}
                loading={index === 0 ? "eager" : "lazy"}
              />
            </figure>
          ))}
        </section>

        <aside className="product-info">
          <div className="product-info-sticky">
            <p className="product-brand">{siteConfig.displayName}</p>
            <div className="product-title-row">
              <h1>{product.name}</h1>
              <button
                type="button"
                className={`product-detail-heart ${isWishlisted(product.id) ? "is-active" : ""}`}
                onClick={() => toggleWishlist(product.id)}
                aria-label={
                  isWishlisted(product.id)
                    ? "Remove from wishlist"
                    : "Add to wishlist"
                }
              >
                <FiHeart aria-hidden="true" />
              </button>
            </div>
            <div className="product-detail-price">
              <span>{formatPrice(product.price)}</span>
              {product.compareAtPrice && <del>{formatPrice(product.compareAtPrice)}</del>}
            </div>

            <p className="product-description">{product.description}</p>
            <ul className="product-highlights">
              <li><FiCheck aria-hidden="true" /> Designed with longevity in mind</li>
              <li><FiCheck aria-hidden="true" /> Limited seasonal availability</li>
              <li><FiCheck aria-hidden="true" /> Premium European fabrics</li>
            </ul>

            <fieldset className="product-option-group">
              <legend>Colour: <span>{selectedColor}</span></legend>
              <div className="option-pills color-options">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={selectedColor === color ? "is-selected" : ""}
                    aria-pressed={selectedColor === color}
                    onClick={() => {
                      setSelectedColor(color);
                      setAdded(false);
                    }}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="product-option-group">
              <legend>
                Size: <span>{selectedSize}</span>
                <Link to="/size-guide">Size guide</Link>
              </legend>
              <div className="option-pills size-options">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={selectedSize === size ? "is-selected" : ""}
                    aria-pressed={selectedSize === size}
                    onClick={() => {
                      setSelectedSize(size);
                      setAdded(false);
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="product-purchase-row">
              <div
                className="product-quantity"
                role="group"
                aria-label="Quantity"
              >
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                  aria-label="Decrease quantity"
                >
                  <FiMinus aria-hidden="true" />
                </button>
                <span>{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((value) => value + 1)}
                  aria-label="Increase quantity"
                >
                  <FiPlus aria-hidden="true" />
                </button>
              </div>
              <button
                type="button"
                className="add-to-cart"
                onClick={handleAddToCart}
                disabled={!product.inStock}
              >
                {product.inStock ? "Add to cart" : "Sold out"}
              </button>
            </div>

            {added && (
              <div className="added-notice" role="status">
                <span><FiCheck aria-hidden="true" /> Added to your bag</span>
                <Link to="/cartpage">View bag</Link>
              </div>
            )}

            <div className="product-accordions">
              <details open>
                <summary>Details &amp; care</summary>
                <dl>
                  {Object.entries(product.attributes).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </details>
              <details>
                <summary>Delivery &amp; returns</summary>
                <p>
                  Complimentary tracked delivery over {formatPrice(siteConfig.commerce.freeShippingThreshold)}.
                  Returns are accepted within {siteConfig.commerce.returnsWindowDays} days.
                </p>
              </details>
            </div>
          </div>
        </aside>
      </div>

      {relatedProducts.length > 0 && (
        <section className="related-products" aria-labelledby="related-title">
          <h2 id="related-title">You may also like</h2>
          <div>
            {relatedProducts.map((item) => (
              <article key={item.id}>
                <Link to={`/shopDetails/${item.id}`}>
                  <img
                    src={item.thumbnail}
                    srcSet={`${item.thumbnail} 640w, ${item.image} 1024w`}
                    sizes="(max-width: 699px) 50vw, 34vw"
                    alt={item.name}
                    loading="lazy"
                  />
                  <span>{item.name}</span>
                  <small>{formatPrice(item.price)}</small>
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
